import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../../Services/dataservice.service';
import { AuthModalService } from '../../Services/auth-modal.service';
import { FlowServiceService } from '../../Services/flow-service.service';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-customer-feedback-section',
  templateUrl: './customer-feedback-section.component.html',
  styleUrls: ['./customer-feedback-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerFeedbackSectionComponent implements OnChanges {
  @Input() flowReviews: any[] = [];
  @Input() cachedAverageRating: number = 0;
  @Input() cachedTotalReviews: number = 0;
  @Input() cachedCustomersSaySummary: string = '';
  @Input() cachedReviewsWithImages: any[] = [];
  @Input() cachedTopReviews: any[] = [];
  @Input() hasUserReviewed: boolean = false;
  @Input() userData: any = null; // Current logged-in user data
  @Input() flowData: any = null; // Flow or bundle model (uses rowKey) for ownership / reporting
  @Input() isFlowOwner: boolean = false; // Whether current user is content creator
  /** Optional subtitle under "Customer Feedback" (e.g. bundle vs flow copy). */
  @Input() feedbackSubtitle: string | null = null;
  
  @Output() openReviewDialog = new EventEmitter<void>();
  @Output() markReviewHelpful = new EventEmitter<any>();
  @Output() reportReview = new EventEmitter<any>();
  @Output() showReviewsInfo = new EventEmitter<void>();
  @Output() showFlowIssues = new EventEmitter<void>(); // Emit to show all flow issues
  @Output() reviewResponseSubmitted = new EventEmitter<void>(); // Emit when a review response is submitted
  @Output() openReviewsModal = new EventEmitter<void>(); // Emit to open full reviews modal in parent

  showReviewsExpanded: boolean = false; // Controls visibility of reviews section
  selectedRatingFilter: number | null = null; // Selected rating filter (null = show all)
  topReviewsSort: 'topRated' | 'mostRecent' | 'critical' = 'topRated'; // Sort mode for Top Reviews section
  reviewResponseText: Map<string, string> = new Map(); // Store review response text by review rowKey
  issueResponseText: Map<string, string> = new Map(); // Store issue response text by issue rowKey
  userMarkedHelpful: Set<string> = new Set(); // Track reviews user has marked as helpful (by review rowKey)
  userReportedReviews: Set<string> = new Set(); // Track reviews user has reported (by review rowKey)

  // Load persisted helpful votes from localStorage
  private loadHelpfulVotes(): void {
    if (!this.userData || !this.userData.emailAddress) return;
    
    const storageKey = `helpfulVotes_${this.userData.emailAddress}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const votes = JSON.parse(stored);
        this.userMarkedHelpful = new Set(votes);
      } catch (e) {
        console.error('Error loading helpful votes from localStorage', e);
      }
    }
  }

  // Save helpful votes to localStorage
  private saveHelpfulVotes(): void {
    if (!this.userData || !this.userData.emailAddress) return;
    
    const storageKey = `helpfulVotes_${this.userData.emailAddress}`;
    localStorage.setItem(storageKey, JSON.stringify(Array.from(this.userMarkedHelpful)));
  }

  // Check reported status for all reviews when they're loaded
  checkReportedStatusForReviews(): void {
    if (!this.userData || !this.userData.emailAddress || !this.flowData?.rowKey) {
      return;
    }

    // Check each review to see if user has reported it
    const reviewsToCheck = this.cachedTopReviews || [];
    reviewsToCheck.forEach(review => {
      if (review.rowKey && !this.userReportedReviews.has(review.rowKey)) {
        this.flowServiceService.checkIfUserReportedReview(review.rowKey, this.flowData.rowKey)
          .pipe(take(1))
          .subscribe(
            (response) => {
              if (response && response.hasReported) {
                this.userReportedReviews.add(review.rowKey);
                this.cdr.markForCheck();
              }
            },
            (error) => {
              // Silently fail - don't block UI
              console.error('Error checking reported status', error);
            }
          );
      }
    });
  }

  // Get filtered reviews based on selected rating
  getFilteredReviews(): any[] {
    let reviews = this.cachedTopReviews || [];

    // Filter by selected star rating (from rating bars) if any
    if (this.selectedRatingFilter !== null) {
      reviews = reviews.filter(
        review => Math.round(review.rating || 0) === this.selectedRatingFilter
      );
    }

    // When "Critical Reviews" is selected, only show reviews where issues are reported
    if (this.topReviewsSort === 'critical') {
      reviews = reviews.filter(
        review => review.hasIssues && review.issues && review.issues.length > 0
      );
    }

    const sorted = [...reviews];

    switch (this.topReviewsSort) {
      case 'mostRecent':
        sorted.sort((a, b) => {
          const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return bTime - aTime;
        });
        break;
      case 'critical':
        // For critical, keep only reviews with issues (already filtered) and sort by most recent first
        sorted.sort((a, b) => {
          const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return bTime - aTime;
        });
        break;
      case 'topRated':
      default:
        // Top rated: highest rating first, then newest
        sorted.sort((a, b) => {
          const ratingDiff = (b.rating || 0) - (a.rating || 0);
          if (ratingDiff !== 0) {
            return ratingDiff;
          }
          const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return bTime - aTime;
        });
        break;
    }

    return sorted;
  }

  // Handle Top Reviews sort dropdown change
  onTopReviewsSortChange(sort: 'topRated' | 'mostRecent' | 'critical'): void {
    this.topReviewsSort = sort || 'topRated';
    this.cdr.markForCheck();
  }

  getTopReviewsTitle(): string {
    switch (this.topReviewsSort) {
      case 'mostRecent':
        return 'Most Recent Reviews';
      case 'critical':
        return 'Critical Reviews';
      case 'topRated':
      default:
        return 'Top Rated Reviews';
    }
  }

  // Handle rating bar click to filter reviews
  onRatingBarClick(rating: number): void {
    if (this.selectedRatingFilter === rating) {
      // If clicking the same rating, clear the filter
      this.selectedRatingFilter = null;
    } else {
      // Set the filter to the clicked rating
      this.selectedRatingFilter = rating;
    }
    // Expand reviews section if not already expanded
    if (!this.showReviewsExpanded) {
      this.showReviewsExpanded = true;
    }
    this.cdr.markForCheck();
  }

  // Clear rating filter
  clearRatingFilter(): void {
    this.selectedRatingFilter = null;
    this.cdr.markForCheck();
  }

  // Get all issues from all reviews
  getAllFlowIssues(): any[] {
    if (!this.flowReviews || this.flowReviews.length === 0) return [];
    
    const allIssues: any[] = [];
    this.flowReviews.forEach(review => {
      if (review.hasIssues && review.issues && review.issues.length > 0) {
        review.issues.forEach((issue: any) => {
          allIssues.push({
            ...issue,
            reviewId: review.id || review.rowKey,
            reviewRating: review.rating,
            reviewDate: review.createdDate,
            reviewerName: review.createdByUserName || review.createdByFirstName || 'Anonymous',
            reviewText: review.reviewText
          });
        });
      }
    });
    
    return allIssues;
  }

  // Handle View FAQ button click
  onViewFaqClick(): void {
    this.showFlowIssues.emit();
  }

  // Check if a rating bar is selected
  isRatingSelected(rating: number): boolean {
    return this.selectedRatingFilter === rating;
  }

  // Helper methods for star display
  getStarFill(starIndex: number, rating: number): number {
    if (starIndex <= Math.floor(rating)) {
      return 100; // Fully filled
    } else if (starIndex === Math.ceil(rating) && rating % 1 !== 0) {
      return (rating % 1) * 100; // Partially filled
    }
    return 0; // Empty
  }

  isPartialStar(starIndex: number, rating: number): boolean {
    return starIndex === Math.ceil(rating) && rating % 1 !== 0;
  }

  isFilledStar(starIndex: number, rating: number): boolean {
    return starIndex <= Math.floor(rating);
  }

  getRatingPercentage(starRating: number): number {
    if (!this.flowReviews || this.flowReviews.length === 0) return 0;
    
    const count = this.flowReviews.filter(r => Math.round(r.rating || 0) === starRating).length;
    return this.flowReviews.length > 0 ? Math.round((count / this.flowReviews.length) * 100) : 0;
  }

  getReviewTags(): { label: string; count: number; sentiment?: 'positive' | 'neutral' | 'negative' }[] {
    if (!this.flowReviews || this.flowReviews.length === 0) return [];
    
    const tags: { [key: string]: { count: number; sentiment: 'positive' | 'neutral' | 'negative' } } = {};
    
    // Count tags based on review content and ratings with sentiment
    this.flowReviews.forEach(review => {
      const rating = review.rating || 0;
      const sentiment: 'positive' | 'neutral' | 'negative' = rating >= 4 ? 'positive' : rating >= 3 ? 'neutral' : 'negative';
      
      // Simple keyword extraction (can be enhanced with NLP)
      const text = (review.reviewText || '').toLowerCase();
      const keywords = ['easy', 'helpful', 'clear', 'great', 'excellent', 'confusing', 'difficult', 'unclear', 'poor', 'bad'];
      
      keywords.forEach(keyword => {
        if (text.includes(keyword)) {
          if (!tags[keyword]) {
            tags[keyword] = { count: 0, sentiment };
          }
          tags[keyword].count++;
        }
      });
    });
    
    return Object.entries(tags)
      .map(([label, data]) => ({ label: label.charAt(0).toUpperCase() + label.slice(1), count: data.count, sentiment: data.sentiment }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  hasPositiveTags(): boolean {
    const tags = this.getReviewTags();
    return tags.some(tag => tag.sentiment === 'positive');
  }

  hasNegativeTags(): boolean {
    const tags = this.getReviewTags();
    return tags.some(tag => tag.sentiment === 'negative');
  }

  getInitials(review: any): string {
    const name = review.createdByUserName || review.createdByFirstName || 'Anonymous';
    if (name === 'Anonymous') return 'A';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  getAvatarColor(review: any): string {
    const colors = [
      '#e0e7ff', '#fce7f3', '#fef3c7', '#d1fae5', '#e0f2fe',
      '#f3e8ff', '#fed7aa', '#fecaca', '#c7d2fe', '#bae6fd'
    ];
    const name = review.createdByUserName || review.createdByFirstName || 'Anonymous';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  getReviewTitle(reviewText: string): string {
    if (!reviewText) return '';
    const words = reviewText.split(' ');
    if (words.length <= 5) return reviewText;
    return words.slice(0, 5).join(' ') + '...';
  }

  trackByReviewId(index: number, review: any): any {
    return review.id || review.rowKey || review.createdDate || index;
  }

  trackByTagId(index: number, tag: any): any {
    return tag.label || index;
  }

  onOpenReviewDialog(): void {
    this.openReviewDialog.emit();
  }

  onOpenReviewsModal(): void {
    this.openReviewsModal.emit();
  }

  // Check if user has marked this review as helpful
  hasUserMarkedHelpful(review: any): boolean {
    if (!review || !review.rowKey || !this.userData) {
      return false;
    }
    return this.userMarkedHelpful.has(review.rowKey);
  }

  // Check if user has reported this review
  hasUserReported(review: any): boolean {
    if (!review || !review.rowKey || !this.userData) {
      return false;
    }
    return this.userReportedReviews.has(review.rowKey);
  }

  onMarkReviewHelpful(review: any): void {
    if (!review || !review.rowKey) {
      return;
    }

    // Check if user is logged in
    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("Please login to mark this review as helpful");
      return;
    }

    // Toggle helpful state: pass true to increment, false to decrement
    const markAsHelpful = !this.hasUserMarkedHelpful(review);
    this.flowServiceService.markReviewHelpful(review.rowKey, markAsHelpful)
      .pipe(take(1))
      .subscribe(
        (updatedReview) => {
          // Use backend count so click/unclick correctly increases and decreases the count
          if (updatedReview && typeof updatedReview.helpfulCount === 'number') {
            review.helpfulCount = updatedReview.helpfulCount;
          } else {
            const alreadyMarked = this.hasUserMarkedHelpful(review);
            review.helpfulCount = alreadyMarked
              ? Math.max((review.helpfulCount || 1) - 1, 0)
              : (review.helpfulCount || 0) + 1;
          }

          // Toggle local helpful state (user just toggled the button)
          if (this.hasUserMarkedHelpful(review)) {
            this.userMarkedHelpful.delete(review.rowKey);
          } else {
            this.userMarkedHelpful.add(review.rowKey);
          }
          this.saveHelpfulVotes(); // Persist to localStorage

          this.toastr.success("Thanks for your feedback.");
          this.cdr.markForCheck();

          this.markReviewHelpful.emit(review);
        },
        (error) => {
          console.error("Error marking review helpful", error);
          this.toastr.error("Could not mark as helpful. Please try again.");
        }
      );
  }

  onReportReview(review: any): void {
    if (!review || !review.rowKey) {
      return;
    }

    // Check if user is logged in
    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("Please login to report this review");
      return;
    }

    // Check if user has already reported this review
    if (this.hasUserReported(review)) {
      this.toastr.info("You have already submitted a report for this review.");
      return;
    }

    // Check with backend if user has reported
    if (this.flowData?.rowKey && review.rowKey) {
      this.flowServiceService.checkIfUserReportedReview(review.rowKey, this.flowData.rowKey)
        .pipe(take(1))
        .subscribe(
          (response) => {
            if (response && response.hasReported) {
              // Track locally
              this.userReportedReviews.add(review.rowKey);
              this.toastr.info("You have already submitted a report for this review.");
              this.cdr.markForCheck();
              return;
            }

            // User hasn't reported, emit to parent to open dialog
            this.reportReview.emit(review);
          },
          (error) => {
            console.error("Error checking if user reported review", error);
            // On error, still allow reporting (fail open)
            this.reportReview.emit(review);
          }
        );
    } else {
      // If no flow data, just emit to parent
      this.reportReview.emit(review);
    }
  }

  // Method to track when a review has been reported (called after successful report)
  markReviewAsReported(reviewRowKey: string): void {
    if (reviewRowKey) {
      this.userReportedReviews.add(reviewRowKey);
      this.cdr.markForCheck();
    }
  }

  // Public method to refresh reported status (can be called from parent)
  refreshReportedStatus(): void {
    this.checkReportedStatusForReviews();
  }

  onShowReviewsInfo(): void {
    this.showReviewsInfo.emit();
  }

  toggleReviewsExpanded(): void {
    this.showReviewsExpanded = !this.showReviewsExpanded;
    this.cdr.markForCheck();
  }

  viewUserProfile(review: any): void {
    // Create a user-like object from review data to match search component pattern
    const userEmail = review.createdByUserEmail || review.createdByEmail || review.customerEmail;
    const userName = review.createdByUserName || review.createdByFirstName;
    
    if (!userEmail) {
      console.warn('No user email found in review');
      return;
    }

    // Create a user object similar to what search component expects
    const userObject = {
      customerEmail: userEmail,
      emailAddress: userEmail,
      userName: userName || userEmail.split('@')[0],
      firstName: userName || userEmail.split('@')[0],
      profilePicUrl: review.profilePicUrl || null
    };

    // Use the same pattern as search component
    if (this.userData != null) {
      // Pass user data via dataService BEFORE navigation to ensure it's available when component loads
      this.dataService.flowByCategorieschangeMessage(userObject);
      
      if (
        this.userData.emailAddress === userObject.customerEmail ||
        this.userData.emailAddress === userObject.emailAddress
      ) {
        this.router.navigate(['/user-profile']);
      } else {
        // Navigate to view-profile without username parameter - rely on dataService
        this.router.navigate(['/view-profile']);
      }
    } else {
      // Open registration modal instead of navigating to page (same as search component)
      this.authModalService.openModal('registration', this.router.url);
    }
  }

  getReviewResponse(reviewRowKey: string): string {
    return this.reviewResponseText.get(reviewRowKey) || '';
  }

  setReviewResponse(reviewRowKey: string, value: string): void {
    this.reviewResponseText.set(reviewRowKey, value);
  }

  respondToReview(review: any): void {
    const response = this.reviewResponseText.get(review.rowKey);
    if (!response || !response.trim()) {
      this.toastr.warning("Please enter a response");
      return;
    }

    this.flowServiceService.respondToReview(review.rowKey, response).subscribe(
      (responseData) => {
        this.toastr.success("Response submitted successfully");
        this.reviewResponseText.delete(review.rowKey);
        // Update the review in the local array
        const reviewIndex = this.flowReviews.findIndex(r => r.rowKey === review.rowKey);
        if (reviewIndex !== -1) {
          this.flowReviews[reviewIndex].creatorResponse = responseData.creatorResponse;
          this.flowReviews[reviewIndex].creatorResponseDate = responseData.creatorResponseDate;
        }
        // Emit event to refresh data in parent component
        this.reviewResponseSubmitted.emit();
        this.cdr.markForCheck();
      },
      (error) => {
        this.toastr.error("Error submitting response");
        console.error(error);
      }
    );
  }

  getIssueResponse(issueRowKey: string): string {
    return this.issueResponseText.get(issueRowKey) || '';
  }

  setIssueResponse(issueRowKey: string, value: string): void {
    this.issueResponseText.set(issueRowKey, value);
  }

  respondToIssue(issue: any, review: any): void {
    const response = this.issueResponseText.get(issue.rowKey);
    if (!response || !response.trim()) {
      this.toastr.warning("Please enter a response");
      return;
    }

    this.flowServiceService.respondToIssue(issue.rowKey, response).subscribe(
      (responseData) => {
        this.toastr.success("Response submitted successfully");
        this.issueResponseText.delete(issue.rowKey);
        // Update the issue in the local review's issues array
        const reviewIndex = this.flowReviews.findIndex(r => r.rowKey === review.rowKey);
        if (reviewIndex !== -1 && this.flowReviews[reviewIndex].issues) {
          const issueIndex = this.flowReviews[reviewIndex].issues.findIndex(i => i.rowKey === issue.rowKey);
          if (issueIndex !== -1) {
            this.flowReviews[reviewIndex].issues[issueIndex].creatorResponse = responseData.creatorResponse;
            this.flowReviews[reviewIndex].issues[issueIndex].creatorResponseDate = responseData.creatorResponseDate;
          }
        }
        // Emit event to refresh data in parent component
        this.reviewResponseSubmitted.emit();
        this.cdr.markForCheck();
      },
      (error) => {
        this.toastr.error("Error submitting response");
        console.error(error);
      }
    );
  }

  // Check if the current user is the owner of the review
  isReviewOwner(review: any): boolean {
    if (!review || !this.userData || !this.userData.emailAddress) {
      return false;
    }
    const reviewEmail = review.createdByUserEmail || review.createdByEmail || review.customerEmail;
    return reviewEmail && reviewEmail.toLowerCase() === this.userData.emailAddress.toLowerCase();
  }

  // Handle star click to update rating
  onStarClick(review: any, rating: number): void {
    if (!this.isReviewOwner(review) || !review.rowKey) {
      return;
    }

    // Check if user is logged in
    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("Please login to update your review rating");
      return;
    }

    // Update rating via service
    this.flowServiceService.updateReviewRating(review.rowKey, rating)
      .pipe(take(1))
      .subscribe(
        (updatedReview) => {
          // Update the review in the local arrays
          const reviewIndex = this.flowReviews.findIndex(r => r.rowKey === review.rowKey);
          if (reviewIndex !== -1) {
            this.flowReviews[reviewIndex].rating = updatedReview.rating || rating;
          }
          
          // Update in cachedTopReviews if it exists there
          const cachedIndex = this.cachedTopReviews.findIndex(r => r.rowKey === review.rowKey);
          if (cachedIndex !== -1) {
            this.cachedTopReviews[cachedIndex].rating = updatedReview.rating || rating;
          }

          this.toastr.success("Rating updated successfully");
          this.cdr.markForCheck();
          
          // Emit event to refresh data in parent component (to update average rating)
          this.reviewResponseSubmitted.emit();
        },
        (error) => {
          console.error("Error updating review rating", error);
          const errorMessage = error?.error?.error || error?.message || "Could not update rating. Please try again.";
          this.toastr.error(errorMessage);
        }
      );
  }

  constructor(
    private router: Router,
    private dataService: DataService,
    private authModalService: AuthModalService,
    private cdr: ChangeDetectorRef,
    private flowServiceService: FlowServiceService,
    private toastr: ToastrService
  ) {
    // Load persisted helpful votes on component init
    this.loadHelpfulVotes();
  }

  // Lifecycle hook to check reported status when reviews change
  ngOnChanges(changes: SimpleChanges): void {
    // Reload helpful votes if userData changed
    if (changes['userData']) {
      this.loadHelpfulVotes();
    }
    
    // Check if cachedTopReviews or userData changed
    if (changes['cachedTopReviews'] || changes['userData']) {
      if (this.cachedTopReviews && this.cachedTopReviews.length > 0 && this.userData) {
        // Small delay to ensure everything is initialized
        setTimeout(() => {
          this.checkReportedStatusForReviews();
        }, 100);
      }
    }
  }
}
